import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DisplayLabel } from '../../../../shared/models/displayLabel';
import { Logo } from '../../../../shared/models/logo';
import { StructureDto } from '../../../../shared/models/structure.dto';
import { StructureService } from '../../../../shared/services/structure.service';
import { Utils } from '../../../../shared/utils';
import { StructureValidators } from '../../../../shared/validators/structure.validators';

@Component({
  selector: 'app-structure-global',
  templateUrl: './structure-global.component.html',
  standalone: false,
})
export class StructureGlobalComponent implements OnInit {
  public structureGlobalForm: FormGroup<{
    id: FormControl<number>;
    idIODAS: FormControl<string>;
    label: FormControl<string>;
    SIRET: FormControl<string>;
    fileName: FormControl<string>;
    active: FormControl<boolean>;
    contactEmail: FormControl<string>;
    website: FormControl<string>;
    presentation: FormControl<string>;
    focusAreaIds: FormControl<number[]>;
    portalVisibility: FormControl<boolean>;
    legalName: FormControl<string>;
  }>;

  public logo: Logo;
  public focusAreas: (DisplayLabel & { category?: string })[] = [];
  public selectedFile: File;

  @Input() public structure: StructureDto;

  public submitted: boolean = false;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly structureService: StructureService,
  ) {}

  public async ngOnInit(): Promise<void> {
    this.focusAreas = (await this.structureService.findAllFocusAreas())
      .map((value: DisplayLabel) => ({
        category: 'Tous',
        ...value,
      }))
      .sort((a, b) => (a.label < b.label ? -1 : 1));
    this.loadForm();
  }

  public onSubmit(): boolean {
    this.submitted = true;
    Object.assign(this.structure, this.structureGlobalForm.value, { logo: this.logo });
    return this.structureGlobalForm.valid;
  }

  private loadForm(): void {
    this.logo = this.structure?.logo || undefined;

    this.structureGlobalForm = this.formBuilder.group({
      id: [this.structure.id],
      idIODAS: [this.structure.idIODAS, [Validators.required, StructureValidators.uniqueIdIODAS(this.structureService, this.structure.idIODAS)]],
      label: [
        this.structure.label,
        [
          Validators.required,
          Validators.pattern(Utils.structureLabelPattern),
          StructureValidators.uniqueLabel(this.structureService, this.structure.label),
        ],
      ],
      SIRET: [this.structure.SIRET, [Validators.pattern(Utils.siretPattern)]],
      fileName: [this.logo ? this.logo.name : undefined, []],
      active: [this.structure.active, []],
      contactEmail: [this.structure.contactEmail, []],
      website: [this.structure.website, []],
      presentation: [this.structure.presentation, []],
      focusAreaIds: [this.structure.focusAreaIds, [Validators.required]],
      portalVisibility: [this.structure.portalVisibility, []],
      legalName: [this.structure.legalName, [Validators.required]],
    });
  }

  public async onFileChange(event: Event): Promise<void> {
    this.selectedFile = (event.target as HTMLInputElement)?.files?.[0];
    this.structureGlobalForm.controls.fileName.setValue(this.selectedFile.name);
    this.logo = new Logo(this.selectedFile.name, await this.read(this.selectedFile));
  }

  private async read(file: File): Promise<string> {
    return new Promise<string>((resolve: (value: string) => void): void => {
      const reader: FileReader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (): void => {
        resolve(reader.result as string);
      };
    });
  }
}
